import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import TaskSkeleton from "./TaskSkeleton";
import moment from "moment";

function downloadCSV(rows, filename) {
  const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const MONTHS = [
    { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
    { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
    { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
    { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

function getYears() {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2, current - 3];
}

function getMonthRange(year, month) {
    const start = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    return { start, end };
}

export default function Ojc_time_report() {
    const { http, user } = AuthUser();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedYear, setSelectedYear] = useState("");
    const [assignedTo, setAssignedTo] = useState("");
    const [users, setUsers] = useState([]);

    useEffect(() => {
        http.get(`/department_user_company?login_user=${user.id}`).then((res) => {
            setUsers(res.data.users || []);
        });
    }, [user.id]);

    useEffect(() => {
        fetchReport(startDate, endDate, assignedTo);
    }, [startDate, endDate, assignedTo]);

    const fetchReport = (start = "", end = "", assigneeId = "") => {
        setIsLoading(true);
        const endpoint = `/get_ojc_time_report?login_user=${user.id}&startdate=${start}&enddate=${end}&assigned_to=${assigneeId}`;

        http.get(endpoint)
            .then((res) => {
                setIsLoading(false);
                if (Array.isArray(res.data.data)) {
                    setData(res.data.data);
                } else {
                    setData([]);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching OJC-Time report:", error);
                setData([]);
            });
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
        setSelectedMonth("");
        setSelectedYear("");
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
        setSelectedMonth("");
        setSelectedYear("");
    };

    const handleMonthChange = (event) => {
        const month = event.target.value ? parseInt(event.target.value, 10) : "";
        setSelectedMonth(event.target.value);
        if (month && selectedYear) {
            const { start, end } = getMonthRange(parseInt(selectedYear, 10), month);
            setStartDate(start);
            setEndDate(end);
        } else if (!month) {
            setStartDate("");
            setEndDate("");
        }
    };

    const handleYearChange = (event) => {
        const year = event.target.value;
        setSelectedYear(year);
        if (year && selectedMonth) {
            const { start, end } = getMonthRange(parseInt(year, 10), parseInt(selectedMonth, 10));
            setStartDate(start);
            setEndDate(end);
        } else if (!year) {
            setStartDate("");
            setEndDate("");
        }
    };

    const handleAssignedToChange = (event) => {
        setAssignedTo(event.target.value);
    };

    const handleClear = () => {
        setStartDate("");
        setEndDate("");
        setSelectedMonth("");
        setSelectedYear("");
        setAssignedTo("");
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return "—";
        return moment(dateStr).format("hh:mm A");
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        return moment(dateStr).format("DD MMM, YY");
    };

    const years = getYears();

    return (
        <>
            <div className="container mt-1">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h1 className="mb-0">OJC-Time Report</h1>
                  <button
                    className="btn btn-sm btn-outline-success"
                    disabled={data.length === 0}
                    onClick={() => downloadCSV(
                      [["No", "Assigned To", "OJC No", "Start Time", "End Time", "Date", "Company", "Description"],
                       ...data.map((r, i) => [
                         i + 1, r.assigned_to_name, r.ojc_id,
                         r.start_validated ? formatTime(r.start_otp_validated_time) : "Pending",
                         r.end_validated ? formatTime(r.end_otp_validated_time) : "Pending",
                         formatDate(r.created_at), r.company || "", r.description || ""
                       ])],
                      `ojc_time_report_${moment().format("YYYYMMDD_HHmm")}.csv`
                    )}
                  >⬇ CSV</button>
                </div>
                <p className="text-muted">Start and end times by Assigned To person (OJC job cards only)</p>
                <div className="row align-items-end">
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">Assigned To</label>
                        <select
                            className="form-control"
                            value={assignedTo}
                            onChange={handleAssignedToChange}
                        >
                            <option value="">All</option>
                            {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {[u.name, u.last_name].filter(Boolean).join(" ")}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">Month</label>
                        <select
                            className="form-control"
                            value={selectedMonth}
                            onChange={handleMonthChange}
                        >
                            <option value="">Select month</option>
                            {MONTHS.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">Year</label>
                        <select
                            className="form-control"
                            value={selectedYear}
                            onChange={handleYearChange}
                        >
                            <option value="">Select year</option>
                            {years.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">From Date</label>
                        <input
                            name="start_date"
                            onChange={handleStartDateChange}
                            className="form-control"
                            type="date"
                            value={startDate}
                        />
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2">
                        <label className="form-label">To Date</label>
                        <input
                            name="end_date"
                            onChange={handleEndDateChange}
                            className="form-control"
                            type="date"
                            value={endDate}
                        />
                    </div>
                    <div className="col-md-2 form-group mt-2 mb-2 d-flex align-items-end">
                        <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
                            Clear
                        </button>
                    </div>
                </div>

                <div className="table-responsive">
                    {isLoading ? (
                        <TaskSkeleton />
                    ) : (
                        <table className="table table-striped center_tbl">
                            <thead className="table-primary">
                                <tr>
                                    <th>No</th>
                                    <th>Assigned To</th>
                                    <th>OJC No</th>
                                    <th>Start Time</th>
                                    <th>End Time</th>
                                    <th>Date</th>
                                    <th>Company</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 && !isLoading ? (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted">
                                            No OJC data. Try adjusting filters or ensure OJC job cards have start/end times.
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((row, index) => (
                                        <tr key={row.id}>
                                            <td>{index + 1}</td>
                                            <td className="text-uppercase fw-bold">{row.assigned_to_name}</td>
                                            <td><b>{row.ojc_id}</b></td>
                                            <td>
                                                {row.start_validated ? (
                                                    <span className="text-success">
                                                        <i className="fas fa-check-circle me-1"></i>
                                                        {formatTime(row.start_otp_validated_time)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">Pending</span>
                                                )}
                                            </td>
                                            <td>
                                                {row.end_validated ? (
                                                    <span className="text-danger">
                                                        <i className="fas fa-flag-checkered me-1"></i>
                                                        {formatTime(row.end_otp_validated_time)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">Pending</span>
                                                )}
                                            </td>
                                            <td>{formatDate(row.created_at)}</td>
                                            <td className="text-uppercase">{row.company || "—"}</td>
                                            <td className="font_desc" title={row.description}>
                                                {(row.description || "").slice(0, 40)}
                                                {(row.description || "").length > 40 ? "…" : ""}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
}
